import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip, Divider } from '@mui/material';

const ResultItem = React.memo(({ children }) => (
  <Box
    sx={{
      p: 1.5,
      backgroundColor: 'var(--table-hover-row)',
      borderRadius: 1,
      border: '1px solid var(--table-border)',
    }}
  >
    {children}
  </Box>
));

ResultItem.displayName = 'ResultItem';

const ResultSection = React.memo(({ title, items, itemRenderer }) => {
  if (items.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
        <Chip label={items.length} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {items.map((item, index) => (
          <ResultItem key={item.id || index}>
            {itemRenderer(item)}
          </ResultItem>
        ))}
      </Box>
    </Box>
  );
});

ResultSection.displayName = 'ResultSection';

const FilterResultsDisplay = ({ results, isLoading }) => {
  const { t } = useTranslation();

  const {
    machineGroups = [],
    machines = [],
    locations = [],
    maintenanceTypes = [],
  } = results || {};

  const totalResults = useMemo(
    () => machineGroups.length + machines.length + locations.length + maintenanceTypes.length,
    [machineGroups.length, machines.length, locations.length, maintenanceTypes.length]
  );

  const summaryChips = useMemo(() => {
    const chips = [];
    if (machineGroups.length > 0)
      chips.push(
        <Chip key="machineGroups" label={`${machineGroups.length} ${t('complexFilters.machineGroups')}`} size="small" variant="outlined" />
      );
    if (machines.length > 0)
      chips.push(
        <Chip key="machines" label={`${machines.length} ${t('complexFilters.machines')}`} size="small" variant="outlined" />
      );
    if (locations.length > 0)
      chips.push(
        <Chip key="locations" label={`${locations.length} ${t('complexFilters.locations')}`} size="small" variant="outlined" />
      );
    if (maintenanceTypes.length > 0)
      chips.push(
        <Chip key="maintenanceTypes" label={`${maintenanceTypes.length} ${t('complexFilters.maintenanceTypes')}`} size="small" variant="outlined" />
      );
    return chips;
  }, [machineGroups.length, machines.length, locations.length, maintenanceTypes.length, t]);

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('complexFilters.applyingFilter')}</Typography>
      </Box>
    );
  }

  if (!results) return null;

  if (totalResults === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('complexFilters.noResults')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {t('complexFilters.resultsFound')}:
        </Typography>
        {summaryChips}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <ResultSection
        title={t('complexFilters.machineGroups')}
        items={machineGroups}
        itemRenderer={(item) => (
          <>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>{item.name}</Typography>
            {item.description && (
              <Typography variant="caption" color="text.secondary">{item.description}</Typography>
            )}
          </>
        )}
      />

      <ResultSection
        title={t('complexFilters.machines')}
        items={machines}
        itemRenderer={(item) => (
          <>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {item.name} {item.serialNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.machineGroup && `${t('generic.machineGroup')}: ${item.machineGroup}`}
              {item.machineGroup && item.location && ' • '}
              {item.location && `${t('generic.location')}: ${item.location}`}
            </Typography>
          </>
        )}
      />

      <ResultSection
        title={t('complexFilters.locations')}
        items={locations}
        itemRenderer={(item) => <Typography variant="body2">{item.name}</Typography>}
      />

      <ResultSection
        title={t('complexFilters.maintenanceTypes')}
        items={maintenanceTypes}
        itemRenderer={(item) => (
          <>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>{item.name}</Typography>
            {item.description && (
              <Typography variant="caption" color="text.secondary">{item.description}</Typography>
            )}
          </>
        )}
      />
    </Box>
  );
};

export default React.memo(FilterResultsDisplay);
